/**
 * 📊 Job Details Component
 * Detaillierte Job-Ansicht mit Live-Updates wie im PHP mail_gateway
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  ArrowLeft, 
  Play, 
  Square, 
  Mail, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Activity,
  Users,
  RefreshCw,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadLimit, setLoadLimit] = useState(100);

  // Fetch job details with manual control
  const { data: jobData, isLoading, refetch } = useQuery(
    ['jobDetails', jobId], // Only jobId as dependency
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
        params.append('limit', '1000'); // More results when searching
      } else {
        params.append('limit', loadLimit.toString());
      }
      
      const response = await axios.get(`/api/jobs/${jobId}?${params}`);
      return response.data.data;
    },
    {
      refetchInterval: searchTerm ? 0 : 5000, // No auto-refresh when searching
      enabled: !!jobId,
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      staleTime: 10000 // Data stays fresh for 10 seconds
    }
  );

  const handleJobAction = async (action) => {
    setActionLoading(action);
    try {
      const response = await axios.post(`/api/jobs/${jobId}/${action}`);
      if (response.data.success) {
        toast.success(`Job ${action === 'start' ? 'gestartet' : 'gestoppt'}!`);
        refetch(); // Refresh data
      }
    } catch (error) {
      toast.error(`Fehler beim ${action === 'start' ? 'Starten' : 'Stoppen'} des Jobs`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setLoadLimit(100); // Reset limit when searching
    // Manual refetch after state update
    setTimeout(() => refetch(), 100);
  };

  const handleSearchReset = () => {
    setSearchInput('');
    setSearchTerm('');
    setLoadLimit(100);
    // Manual refetch after state update
    setTimeout(() => refetch(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Extract domain from SMTP server
  const getSmtpDomain = (smtpServer) => {
    if (!smtpServer) return 'N/A';
    // Extract domain from server string like "smtp.gmail.com" or "mail.domain.com:587"
    const domain = smtpServer.split(':')[0]; // Remove port if present
    return domain;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Job nicht gefunden</h3>
        <button
          onClick={() => navigate('/jobs')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Zurück zur Job-Übersicht
        </button>
      </div>
    );
  }

  const { job, logs, stats, pagination } = jobData;

  // Logs are already filtered server-side
  const filteredLogs = logs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/jobs')}
              className="text-gray-400 hover:text-gray-600"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                📊 Job Details
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-300">
                Job ID: {job.job_id}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => refetch()}
              className="text-gray-400 hover:text-gray-600"
              title="Aktualisieren"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            
            {job.status === 'pending' && (
              <button
                onClick={() => handleJobAction('start')}
                disabled={actionLoading === 'start'}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                <span>{actionLoading === 'start' ? 'Starte...' : 'Starten'}</span>
              </button>
            )}
            
            {job.status === 'paused' && (
              <button
                onClick={() => handleJobAction('start')}
                disabled={actionLoading === 'start'}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                <span>{actionLoading === 'start' ? 'Setze fort...' : 'Fortsetzen'}</span>
              </button>
            )}
            
            {job.status === 'processing' && (
              <button
                onClick={() => handleJobAction('stop')}
                disabled={actionLoading === 'stop'}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2 disabled:opacity-50"
              >
                <Square className="h-4 w-4" />
                <span>{actionLoading === 'stop' ? 'Stoppe...' : 'Stoppen'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Job Info & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Information */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">📋 Job-Informationen</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Betreff</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">{job.subject}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <StatusBadge status={job.status} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Absender-Name</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">{job.from_name || 'Nicht gesetzt'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">E-Mails pro Stunde</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">{job.emails_per_hour}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Erstellt</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                {new Date(job.created_at).toLocaleString('de-DE')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Zuletzt aktualisiert</label>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                {new Date(job.updated_at).toLocaleString('de-DE')}
              </p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">📈 Statistiken</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">Gesamt</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-200">{stats.total_recipients}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">Gesendet</span>
              </div>
              <span className="text-lg font-semibold text-green-600">{stats.sent_count}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <XCircle className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-sm text-gray-600">Fehlgeschlagen</span>
              </div>
              <span className="text-lg font-semibold text-red-600">{stats.failed_count}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-sm text-gray-600">Ausstehend</span>
              </div>
              <span className="text-lg font-semibold text-yellow-600">{stats.pending_count}</span>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Erfolgsrate</span>
                <span className="text-xl font-bold text-blue-600">{stats.success_rate}%</span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.success_rate}%` }}
                ></div>
              </div>
            </div>
            
            {/* Versandgeschwindigkeit */}
            {stats.actual_speed_per_hour > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Tatsächliche Versandgeschwindigkeit</span>
                  <span className="text-lg font-bold text-green-600">{stats.actual_speed_per_hour} E-Mails/h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Konfiguriert: {stats.configured_speed} E-Mails/h</span>
                  <span className="text-xs text-gray-500">Letzte 5 Min: {stats.emails_last_5min} E-Mails</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mail Logs */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg overflow-hidden border dark:border-dark-copper-dark">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-copper-dark">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
              <Mail className="h-5 w-5 mr-2 text-blue-600" />
              E-Mail-Logs ({filteredLogs.length}{pagination && searchTerm ? ` von ${pagination.total_available} gefunden` : ` von ${stats.total_recipients} gesamt`})
            </h2>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Nach E-Mail-Adresse suchen..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                Suchen
              </button>
              {searchTerm && (
                <button
                  onClick={handleSearchReset}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>
        </div>

        {logs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-dark-surface">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Empfänger
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    SMTP-Server
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Antwort
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Gesendet
                  </th>
                </tr>
              </thead>
                <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-dark-copper-dark">
                {filteredLogs.map((log, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-50 dark:hover:bg-dark-surface cursor-pointer"
                    onClick={() => setSelectedLog(log)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                      {log.recipient}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={log.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getSmtpDomain(log.smtp_server)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {log.smtp_response_code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(log.created_at).toLocaleString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Keine E-Mails gesendet</h3>
            <p className="mt-1 text-sm text-gray-500">
              {job.status === 'pending' ? 'Job wurde noch nicht gestartet' : 'Noch keine E-Mail-Logs verfügbar'}
            </p>
          </div>
        )}
        
        {/* Load More Button */}
        {pagination && pagination.has_more && !searchTerm && (
          <div className="px-6 py-4 border-t border-gray-200 text-center">
            <button
              onClick={() => {
                const newLimit = loadLimit + 100;
                setLoadLimit(newLimit);
                // Manual refetch with new limit after state update
                setTimeout(() => refetch(), 100);
              }}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Weitere 100 Logs laden ({pagination.current_count} von {pagination.total_available})
            </button>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-secondary rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto border dark:border-dark-copper-mid">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                E-Mail-Details
              </h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">Empfänger</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">{selectedLog.recipient}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">Status</label>
                <div className="mt-1">
                  <StatusBadge status={selectedLog.status} />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">SMTP-Server</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {selectedLog.smtp_server === 'Unbekannt' ? 
                    <span className="text-gray-500 italic">Nicht verfügbar</span> : 
                    selectedLog.smtp_server
                  }
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">Response-Code</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {selectedLog.smtp_response_code === 'N/A' ? 
                    <span className="text-gray-500 italic">Nicht verfügbar</span> : 
                    selectedLog.smtp_response_code
                  }
                </p>
              </div>
              
              {selectedLog.smtp_response_text && selectedLog.smtp_response_text !== 'Keine Details verfügbar' && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">
                    {selectedLog.status === 'FAILED' ? 'Fehlerdetails' : 'SMTP-Antwort'}
                  </label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                      {selectedLog.smtp_response_text}
                    </pre>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-300">Zeitstempel</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                  {new Date(selectedLog.created_at).toLocaleString('de-DE')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="h-3 w-3" /> },
    processing: { color: 'bg-blue-100 text-blue-800', icon: <Activity className="h-3 w-3" /> },
    paused: { color: 'bg-orange-100 text-orange-800', icon: <Square className="h-3 w-3" /> },
    completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> },
    failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="h-3 w-3" /> },
    stopped: { color: 'bg-gray-100 text-gray-800', icon: <Square className="h-3 w-3" /> },
    sent: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" /> }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.icon}
      <span className="ml-1">{status}</span>
    </span>
  );
};

export default JobDetails;
